// Skin undertone detection from the central face region (forehead +
// mid-cheek, excluding eyes and nose-shadow). Classifies warm /
// cool / neutral from the red-to-blue channel ratio of sampled skin
// pixels, then derives per-channel gain factors so the VTO pipeline
// can auto-adjust eyeshadow and blush opacity and warmth.

import type { LipDetection } from '../tracking/faceLandmarker';

// Face oval — covers the full face silhouette
const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

// Inner mask — we skip eyes, brows, nose, and mouth so pigment
// doesn't contaminate the skin sample. Left and right eye are kept as
// separate arrays: a single combined array produces a polygon that
// crosses the bridge of the nose, erroneously excluding healthy skin
// from the sample. Each array is traced as its own subpath inside the
// same beginPath() so evenodd fill carves two independent holes.
const EXCLUDE_LEFT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];
const EXCLUDE_RIGHT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];
const EXCLUDE_MOUTH = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];
const EXCLUDE_BROWS = [
  70, 63, 105, 66, 107,
  336, 296, 334, 293, 300
];

type SkinClass = 'warm' | 'cool' | 'neutral';

export interface SubtoneResult {
  class: SkinClass;
  confidence: number;       // 0..1 how confident the classification is
  warmth: number;           // continuous: > 0 = warm, < 0 = cool
  eyeOpacityGain: number;   // 0.94..1.06 multiplier for eyeshadow opacity
  blushWarmthShift: number; // -0.04..+0.04 hue shift for blush warmth
}

// Lavoriamo a risoluzione fissa per evitare di allocare ImageData full-res
// (un selfie 4032×3024 sono ~48 MB per canvas: due canvas qui = ~96 MB,
// causa concreta di stutter e OOM su Android low-end). 128×128 mantiene
// un campione di alcune migliaia di pixel — più che sufficiente per
// stimare il rapporto R/B della pelle, che converge alla terza cifra
// già a queste risoluzioni.
const SUBTONE_SAMPLE_SIZE = 128;

function sampleSkinRegion(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection
): { r: number; g: number; b: number; samples: number } | null {
  const size = SUBTONE_SAMPLE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, size, size);

  const mask = document.createElement('canvas');
  mask.width = size;
  mask.height = size;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return null;

  // Fill face oval white
  mctx.fillStyle = '#ffffff';
  mctx.beginPath();
  tracePoly(mctx, detection, FACE_OVAL, size, size);
  // Punch out eyes, mouth, brows
  tracePoly(mctx, detection, EXCLUDE_LEFT_EYE, size, size);
  tracePoly(mctx, detection, EXCLUDE_RIGHT_EYE, size, size);
  tracePoly(mctx, detection, EXCLUDE_MOUTH, size, size);
  // Brow-protect dischi: a 128 px il vecchio 0.012 → 1.5 px lasciava quasi
  // tutto il sopracciglio nel sample, contaminando il rapporto R/B con
  // pixel scuri e abbassando luminanza media. 0.05 → 6.4 px copre il bulk
  // del sopracciglio.
  for (const idx of EXCLUDE_BROWS) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const radius = size * 0.05;
    mctx.moveTo(p.x * size + radius, p.y * size);
    mctx.arc(p.x * size, p.y * size, radius, 0, Math.PI * 2);
  }
  mctx.fill('evenodd');

  // A 128×128 sono solo 16k pixel: il loop è cheap e samplare ogni pixel
  // (anziché ogni 4°) dà un'aggregazione più stabile a parità di costo.
  const fullImg = ctx.getImageData(0, 0, size, size);
  const maskImg = mctx.getImageData(0, 0, size, size);
  const fd = fullImg.data;
  const md = maskImg.data;

  let r = 0, g = 0, b = 0, samples = 0;
  for (let i = 0; i < fd.length; i += 4) {
    if (md[i]! < 200) continue; // skip non-skin pixels
    r += fd[i]!;
    g += fd[i + 1]!;
    b += fd[i + 2]!;
    samples++;
  }

  if (samples < 100) return null;
  return { r: r / samples, g: g / samples, b: b / samples, samples };
}

function tracePoly(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  W: number,
  H: number
): void {
  const { landmarks } = detection;
  let started = false;
  for (const idx of indices) {
    const p = landmarks[idx];
    if (!p) continue;
    if (!started) { ctx.moveTo(p.x * W, p.y * H); started = true; }
    else ctx.lineTo(p.x * W, p.y * H);
  }
  if (started) ctx.closePath();
}

export function detectSubtone(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection
): SubtoneResult {
  const sample = sampleSkinRegion(source, detection);

  // Conservative defaults when sampling fails (low light, extreme pose).
  if (!sample) {
    return {
      class: 'neutral',
      confidence: 0,
      warmth: 0,
      eyeOpacityGain: 1,
      blushWarmthShift: 0
    };
  }

  // Normalise RGB by luminance so the ratio is independent of skin depth.
  const { r, g, b } = sample;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  if (lum < 15) {
    // Too dark to be reliable — keep defaults.
    return {
      class: 'neutral',
      confidence: 0.1,
      warmth: 0,
      eyeOpacityGain: 1,
      blushWarmthShift: 0
    };
  }

  const nr = r / lum;
  const nb = b / lum;

  // Raw warmth: positive = more red, negative = more blue.
  const rawWarmth = nr - nb;

  // Normalise to a -1..+1 scale. Empirically, a difference of ~0.15 is a
  // clear warm signal, 0.05 is borderline.
  const warmth = Math.max(-1, Math.min(1, rawWarmth / 0.18));

  let cls: SkinClass;
  if (warmth > 0.28) cls = 'warm';
  else if (warmth < -0.28) cls = 'cool';
  else cls = 'neutral';

  const confidence = Math.max(0, Math.min(1, Math.abs(warmth) / 0.45));

  // Warmer skin: eyeshadow colour already reads as "brighter" against the
  // skin, so we back off opacity slightly. Cooler skin: eyeshadow blends
  // into the skin more, so we boost opacity slightly to keep it visible.
  const eyeOpacityGain = 1 + (cls === 'cool' ? 0.05 : cls === 'warm' ? -0.04 : 0);

  // Warmer skin: shift blush slightly cooler (toward rose) to avoid
  // looking bronzer-like. Cooler skin: shift blush slightly warmer
  // (toward peach) to avoid looking too pink-on-pink.
  const blushWarmthShift = cls === 'warm' ? -0.03 : cls === 'cool' ? 0.04 : 0;

  return { class: cls, confidence, warmth, eyeOpacityGain, blushWarmthShift };
}
